// SectionEditor.jsx — FINAL: REORDER ON DROP ONLY (WORKS!)
import React, { useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MaterialReactTable } from 'material-react-table';
import {
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import HelpTooltip from '../../../components/HelpTooltip';
import SectionDetailPanel from './SectionDetailPanel';

const SectionEditor = ({
  sectionOrder,
  sections,
  setSections,
  headers,
  setHeaders,
  tableRefs,
  work_item_presets,
  isDataReady,
  formKey,
}) => {
  const dispatch = useDispatch();
  const default_profiles = useSelector(state =>
    state.settings_users?.default_profiles || []
  ).filter(p => !['admin', 'super_admin'].includes(p.name.toLowerCase()));

  const MAX_SECTIONS = 10;
  const validSections = useMemo(() => {
    const list = sectionOrder || [];
    const result = [...list];
    while (result.length < MAX_SECTIONS) {
      result.push({
        id: `empty-${result.length}`,
        label: '',
        items: [],
        totaling: { subtotal: false, equation: 'estimate_total' },
        layout: { width: '100%', align: 'left' },
      });
    }
    return result.filter(sec => !sec.id.startsWith('empty-'));
  }, [sectionOrder]);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sectionToDelete, setSectionToDelete] = useState(null);

  const handleDeleteClick = (sectionId) => {
    setSectionToDelete(sectionId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (sectionToDelete) {
      setSections(prev => {
        const updated = { ...prev };
        delete updated[sectionToDelete];
        return updated;
      });
      setHeaders(prev => {
        const updated = { ...prev };
        delete updated[sectionToDelete];
        return updated;
      });
    }
    setDeleteDialogOpen(false);
    setSectionToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSectionToDelete(null);
  };

  // THIS IS THE REAL, WORKING REORDER-ON-DROP
  const muiRowDragHandleProps = useCallback(({ row, table }) => ({
    onDragEnd: () => {
      const { draggingRow, hoveredRow } = table.getState();
      if (!draggingRow || !hoveredRow) return;

      const dragIndex = draggingRow.index;
      const hoverIndex = hoveredRow.index;

      const draggedId = validSections[dragIndex]?.id;
      if (!draggedId || draggedId.startsWith('empty-')) return;

      const newOrder = [...validSections];
      const [moved] = newOrder.splice(dragIndex, 1);
      newOrder.splice(hoverIndex, 0, moved);

      const updated = {};
      newOrder.forEach(sec => {
        if (!sec.id.startsWith('empty-') && sections[sec.id]) {
          updated[sec.id] = sections[sec.id];
        }
      });

      setSections(updated);
    },
  }), [validSections, sections, setSections]);

  const renderDetailPanel = useCallback(({ row }) => {
    const sectionId = row.original.id;
    const items = sections[sectionId]?.items || [];

    return (
      <SectionDetailPanel
        sectionId={sectionId}
        items={items}
        sections={sections}
        setSections={setSections}
        headers={headers}
        setHeaders={setHeaders}
        work_item_presets={work_item_presets}
        default_profiles={default_profiles}
        tableRefs={tableRefs}
      />
    );
  }, [sections, setSections, headers, setHeaders, work_item_presets, default_profiles, tableRefs]);

  if (!isDataReady) return null;

  return (
    <>
      <MaterialReactTable
        key="sections"
        tableInstanceRef={el => (tableRefs.current.sections = el)}
        columns={[
          {
            accessorKey: 'label',
            header: 'Section',
            size: 200,
            Cell: ({ row }) => row.original.label || 'Untitled',
          },
          {
            accessorKey: 'action',
            header: '',
            size: 80,
            Cell: ({ row }) => (
              <IconButton
                size="small"
                color="error"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteClick(row.original.id);
                }}
              >
                <Delete fontSize="small" />
              </IconButton>
            ),
          },
        ]}
        data={validSections.map(sec => ({ id: sec.id, label: sec.label }))}
        enableRowOrdering
        enableRowExpansion
        enableEditing={false}
        getRowId={row => row.id}
        initialState={{ density: 'compact' }}
        muiRowDragHandleProps={muiRowDragHandleProps}
        renderDetailPanel={renderDetailPanel}
      />

      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this section? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SectionEditor;